import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BaseComponent } from '@ptg-shared/components';
import { LifeStatus360ReportService } from '../../services/lifestatus-360-report.service';
import { takeUntil } from 'rxjs/operators';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'ptg-lifestatus-360-report-modal',
  templateUrl: './lifestatus-360-report-modal.component.html',
  styleUrls: ['./lifestatus-360-report-modal.component.scss'],
})
export class LifeStatus360ReportModalComponent extends BaseComponent implements OnInit {
  formGroup: FormGroup;
  benefitBeginDate = new FormControl('', Validators.required);
  isGenerating = false;
  pollInterval: any;

  constructor(
    public dialogRef: MatDialogRef<LifeStatus360ReportModalComponent>,
    private lifeStatus360ReportService: LifeStatus360ReportService,
    private snackBar: MatSnackBar,
    private datePipe: DatePipe
  ) {
    super();

    this.formGroup = new FormGroup({
      benefitBeginDate: this.benefitBeginDate,
    });
  }

  ngOnInit(): void {}

  onCancel(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    this.dialogRef.close();
  }

  onGenerate(): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    this.isGenerating = true;

    // Convert date to UTC
    const benefitBeginDateUTC = new Date(this.benefitBeginDate.value);
    benefitBeginDateUTC.setUTCHours(0, 0, 0, 0);

    const request = {
      benefitBeginDate: benefitBeginDateUTC.toISOString(),
    };

    this.lifeStatus360ReportService
      .generateLifeStatus360Export(request)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.snackBar.open(
            'Life Status 360 report started processing. Once done, system will download it.',
            'Close',
            {
              duration: 5000,
              panelClass: ['success-snackbar'],
            }
          );

          // Start polling for the download
          this.startPolling(response.token);
        },
        error: (error) => {
          this.isGenerating = false;
          this.snackBar.open(
            'Error generating report. Please try again.',
            'Close',
            {
              duration: 5000,
              panelClass: ['error-snackbar'],
            }
          );
        },
      });
  }

  private startPolling(token: string): void {
    let attempts = 0;
    const maxAttempts = 300; // Poll for 5 minutes (300 attempts * 1 second)

    this.pollInterval = setInterval(() => {
      attempts++;

      if (attempts > maxAttempts) {
        clearInterval(this.pollInterval);
        this.isGenerating = false;
        this.snackBar.open(
          'Report generation is taking longer than expected. Please check back later.',
          'Close',
          {
            duration: 5000,
            panelClass: ['warning-snackbar'],
          }
        );
        this.dialogRef.close();
        return;
      }

      this.lifeStatus360ReportService
        .downloadLifeStatus360Export(token)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (blob) => {
            if (blob && blob.size > 0) {
              // File is ready, download it
              clearInterval(this.pollInterval);
              this.downloadFile(blob);
              this.isGenerating = false;
              this.snackBar.open('Report downloaded successfully!', 'Close', {
                duration: 3000,
                panelClass: ['success-snackbar'],
              });
              this.dialogRef.close();
            }
          },
          error: (error) => {
            // 204 No Content means file is not ready yet, continue polling
            if (error.status !== 204) {
              // Actual error occurred
              clearInterval(this.pollInterval);
              this.isGenerating = false;
              this.snackBar.open(
                'Error downloading report. Please try again.',
                'Close',
                {
                  duration: 5000,
                  panelClass: ['error-snackbar'],
                }
              );
              this.dialogRef.close();
            }
            // If 204, continue polling
          },
        });
    }, 1000); // Poll every 1 second
  }

  private downloadFile(blob: Blob): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const timestamp = this.datePipe.transform(new Date(), 'yyyyMMdd_HHmmss');
    link.download = `LifeStatus_360_Report_${timestamp}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  ngOnDestroy(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
    super.ngOnDestroy();
  }
}
